import { useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: ReactNode;
}

export interface SegmentedProps<T extends string> {
  readonly options: readonly SegmentedOption<T>[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  /** Accessible name of the group. Ignored when `labelledBy` is set. */
  readonly label?: string | undefined;
  /** Id of a visible heading that names the group. */
  readonly labelledBy?: string | undefined;
  /** Data attribute that carries each option's value, e.g. `data-theme-option` (for tests / e2e). */
  readonly valueAttribute?: `data-${string}` | undefined;
  readonly className?: string | undefined;
}

const NEXT_KEYS = new Set(['ArrowRight', 'ArrowDown']);
const PREV_KEYS = new Set(['ArrowLeft', 'ArrowUp']);

/**
 * Segmented control as a WAI-ARIA radio group: every choice is a visible `role="radio"` button you
 * can click directly; one tab stop (the checked option); arrow keys / Home / End move and select.
 * Same `options` / `value` / `onChange` shape as the app's ThemeSwitch. Styles: `.dcs-segmented`.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  labelledBy,
  valueAttribute,
  className,
}: SegmentedProps<T>) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const checkedAt = options.findIndex((option) => option.value === value);
  const tabStop = checkedAt === -1 ? 0 : checkedAt;

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const last = options.length - 1;
    let next: number;
    if (NEXT_KEYS.has(event.key)) next = checkedAt >= last ? 0 : checkedAt + 1;
    else if (PREV_KEYS.has(event.key)) next = checkedAt <= 0 ? last : checkedAt - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else return;
    event.preventDefault();
    const option = options[next];
    if (option === undefined) return;
    onChange(option.value);
    buttons.current[next]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={labelledBy === undefined ? label : undefined}
      aria-labelledby={labelledBy}
      className={className === undefined ? 'dcs-segmented' : `dcs-segmented ${className}`}
      onKeyDown={onKeyDown}
    >
      {options.map((option, index) => {
        const checked = index === checkedAt;
        const extra = valueAttribute === undefined ? {} : { [valueAttribute]: option.value };
        return (
          <button
            key={option.value}
            ref={(node) => {
              buttons.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={index === tabStop ? 0 : -1}
            className="dcs-segmented__option"
            onClick={() => {
              onChange(option.value);
            }}
            {...extra}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
